import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';

const V = {
  ink:    '#0c1830', ink2: '#2a3956', ink3: '#5a6680', ink4: '#8a93a6',
  paper:  '#fbfaf6', paper2: '#f3f1ea', paper3: '#e6e2d5', line: '#d8d3c2',
  gold:   '#c8a64b', good: '#2a6f4f', goodSoft: '#e8f0eb', bad: '#9c2b1a', badSoft: '#f4e8e5',
  warn:   '#a8731a', warnSoft: '#f5ecd9',
  serif:  "'Source Serif 4', Georgia, serif",
  sans:   "'Inter', -apple-system, system-ui, sans-serif",
  mono:   "'JetBrains Mono', ui-monospace, monospace",
};

function FontLoader() {
  useEffect(() => {
    if (document.getElementById('verdict-fonts')) return;
    const link = document.createElement('link');
    link.id = 'verdict-fonts';
    link.href = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }, []);
  return null;
}

function formatValue(val, currency) {
  if (!val) return '—';
  const sym = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';
  return `${sym}${Number(val).toLocaleString('en-GB')}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ContractLog() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const isGlobal = !projectId;

  useEffect(() => {
    const token = localStorage.getItem('clarix_token');
    const url = projectId ? `${API}/api/documents/contracts?project_id=${projectId}` : `${API}/api/documents/contracts`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => { setContracts(data.contracts || []); setLoading(false); });
  }, [projectId]);

  const totalValue   = contracts.reduce((sum, c) => sum + (c.total_value || 0), 0);
  const awardedCount = contracts.filter(c => c.awarded === 1).length;

  return (
    <div style={s.wrap}>
      <FontLoader />

      {/* Util bar */}
      <div style={s.utilBar}>
        <div style={s.utilDot}></div>
        <span style={s.utilLabel}>LIVE INTEL</span>
        <span style={{ fontSize: 11, color: 'rgba(251,250,246,0.5)', fontFamily: V.sans }}>
          {isGlobal ? 'Global Contract Log' : 'Project Contract Log'} · {contracts.length} contracts
        </span>
      </div>

      {/* Top nav */}
      <div style={s.nav}>
        <div style={s.wordmark} onClick={() => navigate('/')}><span style={{ color: V.gold }}>V</span>erdict</div>
        <div style={s.navDivider}></div>
        <div style={s.navTag}>Negotiation Intelligence</div>
        <div style={s.navItems}>
          {['Negotiations','The Room','Suppliers','Documents','Reports'].map(item => (
            <div key={item} style={{ ...s.navItem, color: item === 'Negotiations' ? V.ink : V.ink3, borderBottom: item === 'Negotiations' ? `2px solid ${V.gold}` : '2px solid transparent' }}
              onClick={() => {
                if (item === 'Negotiations') navigate('/');
                if (item === 'Suppliers') navigate('/suppliers');
              }}>{item}</div>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {user && <span style={{ fontSize: 12, color: V.ink4 }}>{user.email}</span>}
          {logout && <button style={s.signOut} onClick={logout}>Sign out</button>}
        </div>
      </div>

      {/* Breadcrumb */}
      <div style={s.breadBar}>
        <span style={s.breadLink} onClick={() => navigate('/')}>Negotiations</span>
        <span style={s.breadSep}>/</span>
        {projectId && <><span style={s.breadLink} onClick={() => navigate(`/projects/${projectId}`)}>Project</span><span style={s.breadSep}>/</span></>}
        <span style={s.breadCurrent}>Contract Log</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {projectId && <button style={s.btnGhost} onClick={() => navigate(`/projects/${projectId}`)}>← Dashboard</button>}
          {projectId && <button style={s.btnGhost} onClick={() => navigate(`/projects/${projectId}/timeline`)}>← Timeline</button>}
        </div>
      </div>

      <div style={s.body}>
        {/* Page heading */}
        <div style={{ paddingBottom: 20, borderBottom: `1px solid ${V.line}`, marginBottom: 24 }}>
          <div style={s.pageEyebrow}>{isGlobal ? 'GLOBAL CONTRACT LOG' : 'CONTRACT LOG'}</div>
          <div style={s.pageTitle}>
            {loading ? 'Loading…' : `${contracts.length} contract${contracts.length !== 1 ? 's' : ''} on record.`}
          </div>
          <div style={s.pageSub}>
            {isGlobal ? 'All contracts across all negotiations' : `All contracts for this negotiation · ${awardedCount} awarded`}
          </div>
        </div>

        {/* KPIs */}
        <div style={s.kpis}>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>TOTAL CONTRACTS</div>
            <div style={s.kpiVal}>{contracts.length}</div>
            <div style={s.kpiSub}>document type: Contract</div>
          </div>
          <div style={s.kpi}>
            <div style={s.kpiLabel}>AWARDED</div>
            <div style={{ ...s.kpiVal, color: V.good }}>{awardedCount}</div>
            <div style={s.kpiSub}>{contracts.length - awardedCount} pending decision</div>
          </div>
          <div style={{ ...s.kpi, borderTop: `2px solid ${V.gold}` }}>
            <div style={s.kpiLabel}>TOTAL VALUE</div>
            <div style={{ ...s.kpiVal, color: V.gold, fontSize: totalValue > 999999 ? 24 : 32 }}>
              {totalValue > 0 ? formatValue(totalValue, 'EUR') : '—'}
            </div>
            <div style={s.kpiSub}>across all contracts</div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={s.empty}>Loading contracts…</div>
        ) : contracts.length === 0 ? (
          <div style={s.empty}>
            No contracts found. Upload a document with type "Contract" to populate this log.
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${V.line}`, background: V.paper2 }}>
                  <th style={s.th}>Contract</th>
                  {isGlobal && <th style={s.th}>Project</th>}
                  <th style={s.th}>Supplier</th>
                  <th style={s.th}>Date</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Value</th>
                  <th style={s.th}>Payment terms</th>
                  <th style={s.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c, i) => (
                  <tr key={c.id} style={{ borderBottom: `0.5px solid ${V.line}`, background: i % 2 === 0 ? V.paper : V.paper2 }}>
                    <td style={s.tdMain}>
                      <div style={{ fontFamily: V.mono, fontSize: 12, fontWeight: 500, color: V.ink }}>{c.original_name}</div>
                      {c.contract_number && <div style={{ fontSize: 10, color: V.ink4, marginTop: 2 }}>{c.contract_number}</div>}
                    </td>
                    {isGlobal && (
                      <td style={s.td}>
                        <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: c.industry === 'maritime' ? '#1a4d8f' : c.industry === 'construction' ? V.warn : V.ink3, marginBottom: 3 }}>
                          {c.industry === 'maritime' ? '⚓ Maritime' : c.industry === 'construction' ? '🏗 Construction' : '◈ General'}
                        </div>
                        <div style={{ fontSize: 12, color: V.ink2 }}>{c.project_name}</div>
                      </td>
                    )}
                    <td style={s.td}>{c.supplier || '—'}</td>
                    <td style={{ ...s.td, fontFamily: V.mono, fontSize: 11 }}>{formatDate(c.document_date)}</td>
                    <td style={{ ...s.td, fontFamily: V.mono, fontSize: 13, fontWeight: 500, color: c.awarded ? V.gold : V.ink2, textAlign: 'right' }}>
                      {formatValue(c.total_value, c.currency)}
                    </td>
                    <td style={s.td}>{c.payment_terms || '—'}</td>
                    <td style={s.td}>
                      {c.awarded === 1
                        ? <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 3, color: V.good, background: V.goodSoft }}>✓ Awarded</span>
                        : <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 3, color: V.warn, background: V.warnSoft }}>○ Pending</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  wrap:        { minHeight: '100vh', background: V.paper2, fontFamily: V.sans, color: V.ink },
  utilBar:     { background: V.ink, height: 32, display: 'flex', alignItems: 'center', padding: '0 24px', gap: 12 },
  utilDot:     { width: 6, height: 6, borderRadius: '50%', background: V.gold },
  utilLabel:   { fontSize: 9, fontWeight: 700, letterSpacing: 2, color: V.gold, textTransform: 'uppercase' },
  nav:         { background: V.paper, borderBottom: `1px solid ${V.line}`, height: 60, display: 'flex', alignItems: 'center', padding: '0 24px' },
  wordmark:    { fontFamily: V.serif, fontSize: 26, fontWeight: 600, letterSpacing: -0.5, color: V.ink, cursor: 'pointer' },
  navDivider:  { width: 1, height: 18, background: V.line, margin: '0 12px' },
  navTag:      { fontSize: 10, fontWeight: 500, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink3, marginRight: 32, whiteSpace: 'nowrap' },
  navItems:    { display: 'flex', height: 60, alignItems: 'center' },
  navItem:     { padding: '0 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer', height: 60, display: 'flex', alignItems: 'center' },
  signOut:     { background: 'none', border: 'none', fontSize: 12, color: V.ink3, cursor: 'pointer' },
  breadBar:    { background: V.paper, borderBottom: `1px solid ${V.line}`, padding: '0 48px', height: 52, display: 'flex', alignItems: 'center', gap: 10 },
  breadLink:   { fontSize: 13, color: V.ink4, cursor: 'pointer' },
  breadSep:    { fontSize: 13, color: V.line },
  breadCurrent:{ fontSize: 13, color: V.ink, fontWeight: 500 },
  btnGhost:    { background: 'transparent', border: `1px solid ${V.line}`, borderRadius: 4, padding: '7px 14px', fontSize: 12, cursor: 'pointer', color: V.ink2 },
  body:        { padding: '32px 48px 80px' },
  pageEyebrow: { fontSize: 9, fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase', color: V.ink4, marginBottom: 8 },
  pageTitle:   { fontFamily: V.serif, fontSize: 34, fontWeight: 400, color: V.ink, letterSpacing: -0.5, marginBottom: 6 },
  pageSub:     { fontSize: 13, color: V.ink4 },
  kpis:        { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: V.line, border: `1px solid ${V.line}`, borderRadius: 4, overflow: 'hidden', marginBottom: 24 },
  kpi:         { background: V.paper, padding: '20px 24px', borderTop: `2px solid ${V.line}` },
  kpiLabel:    { fontSize: 9, fontWeight: 600, letterSpacing: 2, color: V.ink4, marginBottom: 10, textTransform: 'uppercase' },
  kpiVal:      { fontFamily: V.mono, fontSize: 32, fontWeight: 500, lineHeight: 1, letterSpacing: -1, color: V.ink },
  kpiSub:      { fontSize: 11, color: V.ink4, marginTop: 6 },
  tableWrap:   { background: V.paper, border: `0.5px solid ${V.line}`, borderRadius: 6, overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '11px 14px', textAlign: 'left', fontSize: 9, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase', color: V.ink4 },
  tdMain:      { padding: '14px', verticalAlign: 'middle' },
  td:          { padding: '14px', fontSize: 13, color: V.ink2, verticalAlign: 'middle' },
  empty:       { textAlign: 'center', padding: '60px 0', color: V.ink4, fontSize: 14 },
};
